import { useCallback } from 'react';
import { createFileRoute, Outlet } from '@tanstack/react-router';

import type { SortDirection } from '~/api/types';
import type { DepartmentTableColumnKey } from '~/components/table/department/types';
import { USER_ROLES } from '~/features/auth/roles';
import {
  CUSTOMER_ADMIN_DEPARTMENTS_TABS,
  type CustomerAdminDepartmentsTabsValue,
} from '~/features/customerAdministration/departments/constants';
import { CustomerAdminDepartmentsScreen } from '~/features/customerAdministration/departments/CustomerAdminDepartmentsScreen';
import { CustomerAdminDepartmentsSearchParamsProvider } from '~/features/customerAdministration/departments/params';
import type {
  CustomerAdminDepartmentsSearchParams,
  CustomerAdminDepartmentsSearchParamsHandler,
} from '~/features/customerAdministration/departments/types';
import { protectedRoute } from '~/utils/routesAuthorization';

const TEST_ID = 'customer-administration-departments';

export const Route = createFileRoute('/customer-administration/departments')({
  component: RootComponent,
  validateSearch: (search): CustomerAdminDepartmentsSearchParams => {
    // Validate and parse the search params into a typed state
    const defaultSortDir: SortDirection =
      search.sortDir === 'asc' ? 'asc' : 'desc';
    const sortDir = search.sortDir ? defaultSortDir : undefined;

    return {
      page: search.page ? Number(search.page) || 1 : undefined,
      sort: (search.sort as DepartmentTableColumnKey | undefined) ?? undefined,
      search:
        search.search && typeof search.search === 'string'
          ? decodeURI(search.search)
          : undefined,
      sortDir,
      filters: search.filters ?? undefined,
      tab:
        (search.tab as CustomerAdminDepartmentsTabsValue) ||
        CUSTOMER_ADMIN_DEPARTMENTS_TABS.byIDN,
    };
  },
  beforeLoad: () => {
    protectedRoute({
      roles: [USER_ROLES.AdminAbbott, USER_ROLES.AbbottUser],
    });
  },
});

function RootComponent() {
  const navigate = Route.useNavigate();

  const onSearchParamsChange: CustomerAdminDepartmentsSearchParamsHandler =
    useCallback((onSearch) => navigate({ search: onSearch }), [navigate]);

  return (
    <CustomerAdminDepartmentsSearchParamsProvider
      params={Route.useSearch()}
      onSearchParamsChange={onSearchParamsChange}
    >
      <CustomerAdminDepartmentsScreen testID={TEST_ID}>
        <Outlet />
      </CustomerAdminDepartmentsScreen>
    </CustomerAdminDepartmentsSearchParamsProvider>
  );
}
